
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MAIN_COUPON_CODE } from '../constants';
import { ArrowLeft, Check, X, MapPin, Monitor, Zap, Info, Copy, CheckCircle2, Star, Users } from 'lucide-react';

const VidyapeethVsOnline: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(MAIN_COUPON_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const comparisonData = [
    { feature: 'Cost / Fees', offline: 'Higher (Includes infrastructure & center costs)', online: 'Very Low (Extremely pocket-friendly)' },
    { feature: 'Environment', offline: 'Physical classroom, strict schedule', online: 'Study from home, flexible schedule' },
    { feature: 'Doubt Solving', offline: 'Face-to-face with faculty at the center', online: 'Advanced doubt engine & Saarthi mentorship' },
    { feature: 'Study Material', offline: 'Physical modules provided at center', online: 'PDFs + Option to order physical books' },
    { feature: 'Faculty', offline: 'Star faculty at specific centers', online: 'India\'s top educators (accessible everywhere)' },
    { feature: 'Tests', offline: 'In-person center-based testing', online: 'Online AI-based analytics testing' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-white mb-12 transition-colors">
        <ArrowLeft size={16} /> Back to Home
      </Link>

      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-400 text-xs font-black mb-6 tracking-[0.2em] uppercase">
          Student Comparison Guide
        </div>
        <h1 className="text-5xl md:text-7xl font-black mb-6 uppercase tracking-tighter italic">
          Vidyapeeth <span className="text-blue-500 not-italic">vs</span> Online
        </h1>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto font-medium leading-relaxed">
          Struggling to choose between Physics Wallah's premium offline centers and their world-class online batches? We've broken it down for you.
        </p>
      </div>

      {/* Main Comparison Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        <div className="bg-[#111] border border-white/5 p-8 md:p-12 rounded-[40px] relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-20 transition-opacity">
             <MapPin size={120} />
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl font-black text-white mb-6 uppercase tracking-tight flex items-center gap-3">
              <span className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-500 text-sm">OFF</span>
              PW Vidyapeeth
            </h2>
            <p className="text-gray-400 mb-8 font-medium">The traditional offline coaching experience powered by technology. Best for students who need a physical presence and peer interaction.</p>
            <ul className="space-y-4 mb-10">
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">In-person mentorship & discipline</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">Dedicated doubt-solving counters</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">Physical interaction with top-rankers</span>
              </li>
            </ul>
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-6 rounded-3xl">
              <h4 className="text-yellow-500 text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-2"><Info size={14} /> Vidyapeeth Savings</h4>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-tight">
                Yes! Our code <span className="text-yellow-500 font-black">{MAIN_COUPON_CODE}</span> can be used for Vidyapeeth admissions too. Simply provide it at the center or use it when booking via the PW app.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#111] border border-blue-600/20 p-8 md:p-12 rounded-[40px] relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-20 transition-opacity">
             <Monitor size={120} />
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl font-black text-white mb-6 uppercase tracking-tight flex items-center gap-3">
              <span className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center text-blue-400 text-sm">ON</span>
              PW Online Batches
            </h2>
            <p className="text-gray-400 mb-8 font-medium">The revolution of affordable education. High-quality lectures delivered directly to your device with maximum flexibility.</p>
            <ul className="space-y-4 mb-10">
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">Most affordable way to study (Use JITJAN0001)</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">Watch lectures anytime, anywhere</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full"><Check size={14} className="text-green-500" /></div>
                <span className="text-gray-300 font-bold text-sm">Access to ALL of India's top faculty</span>
              </li>
            </ul>
            <div className="bg-blue-600/10 border border-blue-600/20 p-6 rounded-3xl">
              <h4 className="text-blue-400 text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-2"><Zap size={14} /> Max Savings Tip</h4>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-tight italic">Online batches support coupon JITJAN0001 for immediate max discount application.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-[#0f0f0f] border border-white/10 rounded-[40px] overflow-hidden shadow-2xl mb-20">
        <div className="p-10 border-b border-white/5">
           <h3 className="text-2xl font-black text-white uppercase tracking-tight">Direct Feature Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/5 text-[10px] font-black uppercase tracking-[0.2em] text-gray-500">
                <th className="px-10 py-6">Feature</th>
                <th className="px-10 py-6">Vidyapeeth (Offline)</th>
                <th className="px-10 py-6">Online Batches</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {comparisonData.map((row, i) => (
                <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-10 py-6 font-bold text-blue-400 uppercase text-xs tracking-widest">{row.feature}</td>
                  <td className="px-10 py-6 text-sm text-gray-400 font-medium">{row.offline}</td>
                  <td className="px-10 py-6 text-sm text-white font-bold">{row.online}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Decision Guide */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        <div className="p-10 bg-white/5 border border-white/10 rounded-[40px] text-center flex flex-col items-center">
          <Users size={48} className="text-blue-500 mb-6" />
          <h4 className="text-xl font-bold mb-4 uppercase">Choose Offline If...</h4>
          <p className="text-sm text-gray-500 font-medium italic">"I need the physical presence of teachers and a fixed environment to stay disciplined."</p>
        </div>
        <div className="p-10 bg-blue-600/10 border border-blue-600/20 rounded-[40px] text-center flex flex-col items-center scale-105 shadow-2xl shadow-blue-600/5">
          <Zap size={48} className="text-blue-400 mb-6 animate-pulse" />
          <h4 className="text-xl font-bold mb-4 uppercase text-white">Choose Online If...</h4>
          <p className="text-sm text-gray-300 font-bold uppercase tracking-tight">"I want top-tier education at the most affordable price from the comfort of my home."</p>
        </div>
        <div className="p-10 bg-white/5 border border-white/10 rounded-[40px] text-center flex flex-col items-center">
          <Star size={48} className="text-yellow-500 mb-6" />
          <h4 className="text-xl font-bold mb-4 uppercase">Choose Hybrid If...</h4>
          <p className="text-sm text-gray-500 font-medium italic">"I want the structure of a center but the flexibility of online backup classes."</p>
        </div>
      </div>

      {/* Call to Action Card */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-[50px] p-12 md:p-20 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter mb-8 italic">
            Ready to start <br /> your journey?
          </h2>
          <p className="text-lg text-white/80 font-bold mb-12 uppercase tracking-tight">
            Apply the official student referral code to get the maximum rebate on both online and Vidyapeeth batches today.
          </p>
          
          <div className="flex flex-col items-center gap-6">
            <div className="bg-black/40 border border-white/10 px-10 py-5 rounded-[30px] flex items-center justify-between w-full max-w-sm backdrop-blur-xl">
              <span className="text-3xl font-mono font-black text-blue-400 tracking-tighter">{MAIN_COUPON_CODE}</span>
              <button 
                onClick={handleCopy}
                className={`p-3 rounded-2xl transition-all active:scale-95 ${copied ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
              >
                {copied ? <CheckCircle2 size={24} /> : <Copy size={24} />}
              </button>
            </div>
            <p className="text-xs text-white/50 font-black uppercase tracking-[0.2em]">Verified Ambassador Code 2026</p>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-4">
             <Link to="/pw-coupon-code" className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-black uppercase text-sm tracking-widest hover:bg-gray-100 transition-all shadow-xl">
                Browse Online Batches
             </Link>
             <a href="https://www.pw.live" target="_blank" className="bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-black uppercase text-sm tracking-widest hover:bg-white/10 transition-all">
                Visit Official Website
             </a>
          </div>
        </div>
      </div>
      
      {/* Student Review Section (Mini) */}
      <div className="mt-24 text-center">
         <h3 className="text-gray-500 text-[10px] font-black uppercase tracking-[0.5em] mb-12">Trusted by 10 Lakh+ Aspirants</h3>
         <div className="flex justify-center gap-8 opacity-40 grayscale">
            <span className="font-black text-2xl tracking-tighter italic">IIT JEE</span>
            <span className="font-black text-2xl tracking-tighter italic">NEET</span>
            <span className="font-black text-2xl tracking-tighter italic">UPSC</span>
            <span className="font-black text-2xl tracking-tighter italic">GATE</span>
         </div>
      </div>
    </div>
  );
};

export default VidyapeethVsOnline;
